import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import DataTable from '../components/common/DataTable';
import { positionService, departmentService } from '../services/hrService';
import { formatCurrency } from '../utils/format';

export default function Positions() {
  const [positions, setPositions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    code: '',
    description: '',
    department_id: '',
    min_salary: '',
    max_salary: ''
  });

  useEffect(() => {
    fetchPositions();
    fetchDepartments();
  }, []);

  const fetchPositions = async () => {
    try {
      setLoading(true);
      const response = await positionService.getAll({ search: searchTerm });
      setPositions(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching positions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await departmentService.getAll();
      setDepartments(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPositions();
  };

  const openModal = (position = null) => {
    if (position) {
      setEditingPosition(position);
      setFormData({
        title: position.title,
        code: position.code || '',
        description: position.description || '',
        department_id: position.department?.id || '',
        min_salary: position.min_salary || '',
        max_salary: position.max_salary || ''
      });
    } else {
      setEditingPosition(null);
      setFormData({
        title: '',
        code: '',
        description: '',
        department_id: '',
        min_salary: '',
        max_salary: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPosition(null);
    setFormData({
      title: '',
      code: '',
      description: '',
      department_id: '',
      salary_grade: '',
      min_salary: '',
      max_salary: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        title: formData.title,
        code: formData.code,
        description: formData.description || '',
        department: formData.department_id || null,
        min_salary: formData.min_salary ? parseFloat(formData.min_salary) : null,
        max_salary: formData.max_salary ? parseFloat(formData.max_salary) : null
      };

      if (editingPosition) {
        await positionService.update(editingPosition.id, submitData);
      } else {
        await positionService.create(submitData);
      }
      closeModal();
      fetchPositions();
    } catch (error) {
      console.error('Error saving position:', error);
      alert(error.response?.data?.detail || JSON.stringify(error.response?.data) || 'Failed to save position');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this position?')) {
      try {
        await positionService.delete(id);
        fetchPositions();
      } catch (error) {
        console.error('Error deleting position:', error);
        alert('Failed to delete position');
      }
    }
  };

  const columns = [
    { header: 'Position Title', accessor: 'title' },
    {
      header: 'Department',
      render: (pos) => pos.department_name || '-'
    },
    {
      header: 'Min Salary',
      render: (pos) => pos.min_salary ? formatCurrency(pos.min_salary) : '-'
    },
    {
      header: 'Max Salary',
      render: (pos) => pos.max_salary ? formatCurrency(pos.max_salary) : '-'
    },
    {
      header: 'Employees',
      render: (pos) => pos.employee_count || 0
    },
    {
      header: 'Actions',
      render: (pos) => (
        <div className="flex space-x-2">
          <button
            onClick={() => openModal(pos)}
            className="text-blue-600 hover:text-blue-900"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDelete(pos.id)}
            className="text-red-600 hover:text-red-900"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Positions</h1>
        <Button onClick={() => openModal()}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Position
        </Button>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
          <form onSubmit={handleSearch} className="flex space-x-4">
            <input
              type="text"
              placeholder="Search positions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <Button type="submit">Search</Button>
          </form>
        </div>

        <DataTable
          columns={columns}
          data={positions}
          loading={loading}
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingPosition ? 'Edit Position' : 'Add Position'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Position Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Position Code *
              </label>
              <input
                type="text"
                required
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Department *
            </label>
            <select
              required
              value={formData.department_id}
              onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select Department</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Minimum Salary
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.min_salary}
                onChange={(e) => setFormData({ ...formData, min_salary: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Maximum Salary
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.max_salary}
                onChange={(e) => setFormData({ ...formData, max_salary: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit">
              {editingPosition ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
